// src/components/common/address/AddressFields.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useRegionalFormDefaults } from '../../../context/RegionalSettingsContext';
import {
    ensureNamedOption,
    filterCities,
    getCountryOptions,
    getStateOptions,
    loadCitiesForState,
} from '../../../utils/geo';

function cityDisplayName(city) {
    return city?.label || city?.name || '';
}

function useDebounced(value, delay = 200) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

/**
 * Address UI with dynamic Country / State / searchable City (country-state-city npm).
 * Stores country, state, and city as display names to match the DB schema.
 */
export default function AddressFields({
    value = {},
    onChange,
    title,
    disabled = false,
    applyDefaultCountry = true,
}) {
    const { t, i18n } = useTranslation();
    const regional = useRegionalFormDefaults();
    const resolvedTitle = title ?? t('forms.address.title');
    const v = {
        addressLine1: value.addressLine1 || '',
        addressLine2: value.addressLine2 || '',
        city: value.city || '',
        state: value.state || '',
        country: value.country || '',
        pincode: value.pincode || '',
        latitude: value.latitude ?? '',
        longitude: value.longitude ?? '',
    };

    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [citiesLoadError, setCitiesLoadError] = useState(false);
    const [cityOpen, setCityOpen] = useState(false);
    const [cityQuery, setCityQuery] = useState(v.city);
    const cityBoxRef = useRef(null);
    const defaultCountryAppliedRef = useRef(false);

    const debouncedCityQuery = useDebounced(cityQuery, 200);

    const setField = (name, next) => {
        if (onChange) onChange({ [name]: next });
    };

    useEffect(() => {
        if (!applyDefaultCountry || !onChange || !regional.countryName || defaultCountryAppliedRef.current) {
            return;
        }
        if (v.country) {
            defaultCountryAppliedRef.current = true;
            return;
        }
        defaultCountryAppliedRef.current = true;
        onChange({ country: regional.countryName });
    }, [applyDefaultCountry, regional.countryName, v.country, onChange]);

    const onCountryChange = (nextCountry) => {
        if (onChange) {
            onChange({ country: nextCountry, state: '', city: '' });
        }
        setCityQuery('');
        setCityOpen(false);
    };

    const onStateChange = (nextState) => {
        setField('state', nextState);
        setField('city', '');
        setCityQuery('');
        setCityOpen(false);
    };

    const countries = useMemo(() => getCountryOptions(), [i18n.language]);

    const countryOptions = useMemo(() => {
        return ensureNamedOption(
            countries.map((c) => ({ name: c.name, label: c.label || c.name })),
            v.country
        );
    }, [countries, v.country]);

    const stateOptions = useMemo(() => {
        const base = getStateOptions(v.country).map((s) => ({
            name: s.name,
            label: s.label || s.name,
        }));
        return ensureNamedOption(base, v.state);
    }, [v.country, v.state, i18n.language]);

    useEffect(() => {
        if (!v.country || !v.state) {
            setCities([]);
            setCitiesLoading(false);
            setCitiesLoadError(false);
            return undefined;
        }

        let cancelled = false;
        setCitiesLoading(true);
        setCitiesLoadError(false);

        loadCitiesForState(v.country, v.state)
            .then((list) => {
                if (!cancelled) {
                    setCities(list);
                    setCitiesLoadError(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setCities([]);
                    setCitiesLoadError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setCitiesLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [v.country, v.state, i18n.language]);

    useEffect(() => {
        const stored = (v.city || '').trim();
        if (!stored) {
            setCityQuery('');
            return;
        }
        const match = cities.find((c) => c.name === stored || c.label === stored);
        setCityQuery(match ? cityDisplayName(match) : stored);
    }, [v.city, cities, i18n.language]);

    useEffect(() => {
        if (!cityOpen) return undefined;

        const onPointerDown = (event) => {
            if (cityBoxRef.current && !cityBoxRef.current.contains(event.target)) {
                setCityOpen(false);
            }
        };

        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [cityOpen]);

    const filteredCities = useMemo(() => {
        const matches = filterCities(cities, debouncedCityQuery, 30);
        const stored = (v.city || '').trim();
        if (stored && !matches.some((c) => c.name === stored || c.label === stored)) {
            const match = cities.find((c) => c.name === stored || c.label === stored);
            return [{
                name: stored,
                label: match ? cityDisplayName(match) : stored,
                legacy: true,
            }, ...matches];
        }
        return matches;
    }, [cities, debouncedCityQuery, v.city]);

    const handleCityInputChange = (next) => {
        setCityQuery(next);
        // Keep English canonical name when the typed text matches a known city label/name.
        const match = cities.find(
            (c) => c.name === next || c.label === next
                || String(c.name || '').toLowerCase() === next.toLowerCase()
                || String(c.label || '').toLowerCase() === next.toLowerCase()
        );
        setField('city', match ? match.name : next);
        setCityOpen(true);
    };

    const handleCitySelect = (city) => {
        const englishName = city?.name || '';
        setCityQuery(cityDisplayName(city) || englishName);
        setField('city', englishName);
        setCityOpen(false);
    };

    const countryHasStates = v.country ? getStateOptions(v.country).length > 0 : false;

    return (
        <div className="form-section">
            {!!resolvedTitle && <h3 className="form-section-title">{resolvedTitle}</h3>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="form-label">{t('forms.address.addressLine1')}</label>
                    <input
                        type="text"
                        value={v.addressLine1}
                        onChange={(e) => setField('addressLine1', e.target.value)}
                        disabled={disabled}
                        className="input"
                        placeholder={regional.addressLine1Placeholder}
                    />
                </div>

                <div>
                    <label className="form-label">{t('forms.address.addressLine2')}</label>
                    <input
                        type="text"
                        value={v.addressLine2}
                        onChange={(e) => setField('addressLine2', e.target.value)}
                        disabled={disabled}
                        className="input"
                        placeholder={t('forms.address.addressLine2Placeholder')}
                    />
                </div>

                <div>
                    <label className="form-label">{t('forms.address.country')}</label>
                    <select
                        value={v.country}
                        onChange={(e) => onCountryChange(e.target.value)}
                        disabled={disabled}
                        className="select"
                    >
                        <option value="">{t('forms.address.selectCountry')}</option>
                        {countryOptions.map((c) => (
                            <option key={c.name} value={c.name}>{c.label || c.name}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="form-label">{t('forms.address.provinceRegion')}</label>
                    <select
                        value={v.state}
                        onChange={(e) => onStateChange(e.target.value)}
                        disabled={disabled || !v.country}
                        className="select"
                    >
                        <option value="">
                            {!v.country
                                ? t('forms.address.selectCountryFirst')
                                : countryHasStates
                                    ? t('forms.address.selectProvince')
                                    : t('forms.address.noRegions')}
                        </option>
                        {stateOptions.map((s) => (
                            <option key={s.name} value={s.name}>{s.label || s.name}</option>
                        ))}
                    </select>
                </div>

                <div ref={cityBoxRef} className="relative">
                    <label className="form-label">{t('forms.address.cityMunicipality')}</label>
                    <input
                        type="text"
                        value={cityQuery}
                        onChange={(e) => handleCityInputChange(e.target.value)}
                        onFocus={() => v.state && setCityOpen(true)}
                        disabled={disabled || !v.state}
                        className="input"
                        placeholder={
                            !v.state
                                ? t('forms.address.selectProvinceFirst')
                                : citiesLoading
                                    ? t('forms.address.loadingCities')
                                    : t('forms.address.searchCity')
                        }
                        autoComplete="off"
                        aria-label={t('forms.address.cityMunicipality')}
                        aria-expanded={cityOpen}
                    />
                    {cityOpen && v.state && !citiesLoading && filteredCities.length > 0 && (
                        <ul
                            className="absolute z-30 top-full left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                            role="listbox"
                        >
                            {filteredCities.map((city) => (
                                <li key={`${city.name}-${city.legacy ? 'legacy' : 'std'}`}>
                                    <button
                                        type="button"
                                        role="option"
                                        onClick={() => handleCitySelect(city)}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 ${
                                            city.name === v.city
                                                ? 'bg-primary-50 text-primary-800 font-medium'
                                                : 'text-gray-800'
                                        }`}
                                    >
                                        {cityDisplayName(city)}
                                        {city.legacy ? ` ${t('forms.address.saved')}` : ''}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {v.state && !citiesLoading && cities.length === 0 && (
                        <p className="field-note mt-1">
                            {citiesLoadError
                                ? t('forms.address.citiesLoadFailed')
                                : t('forms.address.noCityList')}
                        </p>
                    )}
                </div>

                <div>
                    <label className="form-label">{t('forms.address.postcode')}</label>
                    <input
                        type="text"
                        value={v.pincode}
                        onChange={(e) => setField('pincode', e.target.value)}
                        disabled={disabled}
                        className="input"
                        placeholder={regional.postcodePlaceholder}
                    />
                </div>

                <div>
                    <label className="form-label">
                        {t('forms.address.latitude')} <span className="text-gray-400 font-normal">{t('forms.address.optional')}</span>
                    </label>
                    <input
                        type="number"
                        step="0.000001"
                        value={v.latitude}
                        onChange={(e) => setField('latitude', e.target.value)}
                        disabled={disabled}
                        className="input"
                    />
                </div>

                <div>
                    <label className="form-label">
                        {t('forms.address.longitude')} <span className="text-gray-400 font-normal">{t('forms.address.optional')}</span>
                    </label>
                    <input
                        type="number"
                        step="0.000001"
                        value={v.longitude}
                        onChange={(e) => setField('longitude', e.target.value)}
                        disabled={disabled}
                        className="input"
                    />
                </div>
            </div>

            <p className="field-note">
                <Trans i18nKey="forms.address.fillHint" components={{ strong: <strong /> }} />
            </p>
        </div>
    );
}
